import React, { useState, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { X } from "lucide-react";

const MetadataModal = ({
  open,
  onClose,
  onSave,
  assistantColors,
  chatMetadata = {},
  messageMetadata = {},
  chatMetadataReadOnly = false,
}) => {
  const { t } = useTranslation();

  const convertMetadataToBlocks = (metadata) => {
    if (!metadata || typeof metadata !== "object") return [];
    return Object.entries(metadata).map(([key, values], index) => ({
      id: `${key}-${index}`,
      key,
      values: Array.isArray(values) ? values : [values],
    }));
  };

  const convertBlocksToMetadata = (blocks) => {
    const result = {};
    blocks.forEach((block) => { result[block.key] = block.values; });
    return result;
  };

  const [currentChatBlocks, setCurrentChatBlocks] = useState(
    convertMetadataToBlocks(chatMetadata)
  );
  const [currentMessageBlocks, setCurrentMessageBlocks] = useState(
    convertMetadataToBlocks(messageMetadata)
  );

  const handleOpen = () => {
    setCurrentChatBlocks(convertMetadataToBlocks(chatMetadata));
    setCurrentMessageBlocks(convertMetadataToBlocks(messageMetadata));
  };

  const handleSave = () => {
    try {
      onSave({
        chat: convertBlocksToMetadata(currentChatBlocks),
        message: convertBlocksToMetadata(currentMessageBlocks),
      });
    } catch (error) {
      console.error("Error saving metadata:", error);
      alert(t("common.invalid_json"));
    }
  };

  const handleMetadataBlur = (type, index, textValue) => {
    const blocks = type === "chat" ? [...currentChatBlocks] : [...currentMessageBlocks];
    const originalItem = blocks[index];
    try {
      const parsed = JSON.parse(`{${textValue}}`);
      delete parsed._editingText;
      blocks[index] = {
        key: parsed.key || originalItem.key,
        values: Array.isArray(parsed.values) ? parsed.values : [parsed.values || ""],
      };
    } catch {
      blocks[index] = { ...originalItem, _editingText: textValue };
    }
    if (type === "chat") setCurrentChatBlocks(blocks);
    else setCurrentMessageBlocks(blocks);
  };

  const formatMetadataForDisplay = (metadataItem) => {
    if (metadataItem._editingText !== undefined) return metadataItem._editingText;
    const valuesLines = metadataItem.values.map((v) => `    ${JSON.stringify(v)}`).join(",\n");
    return `"key": ${JSON.stringify(metadataItem.key)},\n  "values": [\n${valuesLines}\n  ]`;
  };

  const addMetadataBlock = (type) => {
    const newBlock = {
      id: Math.random().toString(36).substr(2, 9),
      key: t("common.new_key"),
      values: [t("common.new_value")],
    };
    if (type === "chat") setCurrentChatBlocks([...currentChatBlocks, newBlock]);
    else setCurrentMessageBlocks([...currentMessageBlocks, newBlock]);
  };

  const removeMetadataBlock = (type, index) => {
    if (type === "chat") setCurrentChatBlocks(currentChatBlocks.filter((_, i) => i !== index));
    else setCurrentMessageBlocks(currentMessageBlocks.filter((_, i) => i !== index));
  };

  const renderSection = (type, blocks, title, readOnly = false) => (
    <div className="flex-1 flex flex-col min-w-0">
      <h3 className="text-sm font-medium text-gray-900 mb-2">
        {title}
        {readOnly && <span className="text-xs text-gray-400 font-normal ml-2">({t("readOnly")})</span>}
      </h3>
      <div className="border border-gray-200 rounded-xl bg-gray-50/50 flex-1">
        <div className="h-[400px] overflow-y-auto p-3 space-y-2">
          {blocks.map((item, index) => (
            <div
              key={item.id || index}
              className={`relative border rounded-xl flex flex-col ${
                readOnly ? "bg-gray-50 border-gray-200" : "bg-white border-gray-200"
              }`}
              style={{ minHeight: "100px", maxHeight: "130px", overflow: "hidden" }}
            >
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => removeMetadataBlock(type, index)}
                  className="absolute top-2 right-2 z-10 w-5 h-5 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300 text-xs cursor-pointer transition-colors"
                  title={t("removeMetadataBlock")}
                >
                  <X size={12} />
                </button>
              )}
              <textarea
                value={formatMetadataForDisplay(item)}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  const setter = type === "chat" ? setCurrentChatBlocks : setCurrentMessageBlocks;
                  setter((prev) => {
                    const updated = [...prev];
                    updated[index] = { ...updated[index], _editingText: e.target.value };
                    return updated;
                  });
                }}
                onBlur={(e) => handleMetadataBlur(type, index, e.target.value)}
                className={`w-full flex-1 rounded-xl font-mono text-xs focus:outline-none resize-none overflow-y-auto bg-transparent p-3 ${
                  readOnly ? "text-gray-500 cursor-not-allowed" : ""
                }`}
                style={{ paddingRight: readOnly ? "12px" : "32px", wordBreak: "break-all", overflowWrap: "break-word", whiteSpace: "pre-wrap" }}
              />
            </div>
          ))}
          {!readOnly && (
            <button
              type="button"
              onClick={() => addMetadataBlock(type)}
              className="w-full border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-gray-500 hover:border-gray-300 transition-colors cursor-pointer"
              style={{ minHeight: "100px" }}
            >
              <span className="text-lg">+</span>
              <span className="text-xs">{t("upload.add_metadata")}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Transition.Root show={open} as={Fragment} afterEnter={handleOpen}>
      <Dialog as="div" className="relative z-50" initialFocus={false} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative transform bg-white rounded-2xl text-left transition-all w-full max-w-2xl shadow-xl" style={{ maxHeight: "80vh" }}>
                {/* Header */}
                <div className="px-6 pt-6 pb-2">
                  <Dialog.Title as="h2" className="text-lg font-medium text-gray-900">
                    {t("Metadata")}
                  </Dialog.Title>
                </div>

                {/* Content */}
                <div className="px-6 py-4 flex gap-4">
                  {renderSection("chat", currentChatBlocks, t("chatMetadata"), chatMetadataReadOnly)}
                  {renderSection("message", currentMessageBlocks, t("messageMetadata"))}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    {t("cancel")}
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 transition-colors cursor-pointer"
                  >
                    {t("save")}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default MetadataModal;
